/**
 * Node Hover Label Component
 *
 * Displays inline label with name and description when hovering over a node.
 * Uses a portal to render outside ReactFlow's viewport to avoid clipping.
 */
import { memo, useEffect, useState, useRef } from "react";
import { createPortal } from "react-dom";

interface NodeHoverLabelProps {
  /** Node name/label */
  name: string;
  /** Node description */
  description?: string;
  /** Whether the label is currently visible */
  visible: boolean;
  /** Optional position offset from default */
  position?: "below" | "right";
}

export const NodeHoverLabel = memo(
  ({ name, description, visible, position = "below" }: NodeHoverLabelProps) => {
    const [coords, setCoords] = useState<{ x: number; y: number } | null>(null);
    const parentRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
      if (!visible || !parentRef.current) {
        setCoords(null);
        return;
      }

      // Find the node wrapper by traversing up
      const nodeWrapper = parentRef.current.closest("[data-testid^='value-flow-node-']");
      if (!nodeWrapper) {
        setCoords(null);
        return;
      }

      const rect = nodeWrapper.getBoundingClientRect();
      if (position === "right") {
        setCoords({
          x: rect.right + 8,
          y: rect.top + rect.height / 2,
        });
      } else {
        setCoords({
          x: rect.left + rect.width / 2,
          y: rect.bottom + 8,
        });
      }
    }, [visible, position]);

    // Hidden ref element to find parent node
    const refElement = <div ref={parentRef} className="absolute w-0 h-0 pointer-events-none" />;

    if (!visible || !coords) {
      return refElement;
    }

    const tooltip = (
      <div
        style={{
          position: "fixed",
          left: coords.x,
          top: coords.y,
          transform: position === "right" ? "translateY(-50%)" : "translateX(-50%)",
          zIndex: 9999,
        }}
        className={`
          pointer-events-none
          bg-sa-surface/95
          border border-sa-border
          rounded-lg shadow-lg
          px-3 py-2
          min-w-[150px] max-w-[280px]
          animate-in fade-in-0 zoom-in-95 duration-150
        `}
      >
        <div className="text-sm font-semibold text-sa-text leading-tight">{name}</div>
        {description && (
          <div className="text-xs text-sa-text-dim mt-1 leading-relaxed">{description}</div>
        )}
      </div>
    );

    return (
      <>
        {refElement}
        {createPortal(tooltip, document.body)}
      </>
    );
  },
);

NodeHoverLabel.displayName = "NodeHoverLabel";
